import React from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { TypeAnimation } from "react-type-animation";

export default function Onboarding() {
  const onboardingCards = [
    {
      emoji: "💻",
      short: "Code Help",
      title: "Ask Questions About Code",
      text: "Debug code and get programming help.",
      prompts: [
        "Why is my beat not playing?",
        "How do I use makeBeat()?",
        "Can you help fix this loop?",
      ],
    },

    {
      emoji: "🎵",
      short: "Concepts",
      title: "Explain EarSketch Concepts",
      text: "Learn music programming concepts.",
      prompts: [
        "What does fitMedia() do?",
        "Explain beats",
        "What is a list in EarSketch?",
      ],
    },

    {
      emoji: "🛠️",
      short: "Customize",
      title: "Tweak the Agent Behavior",
      text: "Customize how the AI helps you.",
      prompts: [
         "Make it simple",
         "Explain step-by-step",
         "Give me hints instead of answers",
      ],
    },

    {
      emoji: "📚",
      short: "Summaries",
      title: "Summarize Curriculum Content",
      text: "Quickly review lessons and tutorials.",
      prompts: [
        "Summarize this chapter",
        "What are the key ideas?",
        "Give me a quick review",
      ],
    },

    {
      emoji: "🔍",
      short: "Explore",
      title: "Explore Learning Content",
      text: "Understand tutorials and concepts.",
      prompts: [
        "Explain this tutorial",
        "What is this project about?",
        "Help me understand this concept",
      ],
    },
  ];

  const [index, setIndex] = React.useState(0);

  const intervalRef = React.useRef(null);

  const startRotation = React.useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    intervalRef.current = setInterval(() => {
      setIndex((prev) => (prev + 1) % onboardingCards.length);
    }, 6500);
  }, [onboardingCards.length]);

  React.useEffect(() => {
    startRotation();

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [startRotation]);

  const current = onboardingCards[index];

  return (
    <main
      style={{
        minHeight: "100vh",
        background:
          "linear-gradient(to bottom right, #F8FAFC, #EEF2FF)",
        fontFamily: "Inter, sans-serif",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px",
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        style={{
          width: "100%",
          maxWidth: 780,
          textAlign: "center",
        }}
      >
        {/* FLOATING EMOJI */}

        <motion.div
          animate={{ y: [0, -6, 0] }}
          transition={{
            duration: 3,
            repeat: Infinity,
          }}
          style={{
            fontSize: 42,
            marginBottom: 12,
          }}
        >
          🎵
        </motion.div>

        {/* HERO */}

        <h1
          style={{
            fontSize: 44,
            fontWeight: 700,
            color: "#0F172A",
            marginBottom: 12,
            lineHeight: 1.15,
          }}
        >
          Welcome to EarSketch AI 👋
        </h1>

        <p
          style={{
            fontSize: 18,
            lineHeight: 1.6,
            color: "#475569",
            maxWidth: 620,
            marginInline: "auto",
            marginBottom: 28,
          }}
        >
          An AI learning companion for coding, music, and
          creativity.
        </p>

        {/* CAPABILITY TABS */}

        <div style={capabilityWrapper}>
          {onboardingCards.map((item, i) => (
            <motion.div
              key={i}
              whileHover={{
                y: -2,
              }}
              animate={{
                scale: i === index ? 1.04 : 1,
              }}
              transition={{ duration: 0.2 }}
              onClick={() => {
                setIndex(i);
                startRotation();
              }}
              style={{
                ...capabilityTab,
                ...(i === index
                  ? activeCapabilityTab
                  : {}),
              }}
            >
              {item.emoji} {item.short}
            </motion.div>
          ))}
        </div>

        {/* DYNAMIC ONBOARDING CARD */}

        <AnimatePresence mode="wait">
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.35 }}
            style={{
              ...card,
              marginBottom: 34,
              padding: 32,
              minHeight: 320,
              textAlign: "left",
            }}
          >
            {/* CATEGORY */}

            <div
              style={{
                fontSize: 38,
                marginBottom: 16,
              }}
            >
              {current.emoji}
            </div>

            {/* TITLE */}

            <h3
              style={{
                fontSize: 28,
                fontWeight: 700,
                marginBottom: 10,
                color: "#0F172A",
                lineHeight: 1.25,
              }}
            >
              {current.title}
            </h3>

            {/* DESCRIPTION */}

            <p
              style={{
                color: "#475569",
                fontSize: 16,
                marginBottom: 26,
                lineHeight: 1.7,
                maxWidth: 560,
              }}
            >
              {current.text}
            </p>

            {/* LABEL */}

            <div
              style={{
                fontSize: 13,
                color: "#94A3B8",
                marginBottom: 14,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: 1.2,
              }}
            >
              Example Questions
            </div>

            {/* TYPING EXAMPLES */}

            <div
              style={{
                color: "#4338CA",
                fontWeight: 600,
                fontSize: 17,
                lineHeight: 2,
                minHeight: 120,
              }}
            >
              <TypeAnimation
                key={index}
                sequence={[
                  current.prompts[0],
                  1000,

                  `${current.prompts[0]}\n${current.prompts[1]}`,
                  1000,

                  `${current.prompts[0]}\n${current.prompts[1]}\n${current.prompts[2]}`,
                ]}
                speed={58}
                repeat={0}
                cursor={true}
                style={{
                  whiteSpace: "pre-line",
                  display: "block",
                }}
              />
            </div>
          </motion.div>
        </AnimatePresence>

        {/* BUTTON */}

        <Link to="/chat" className="start-btn">
          Start Using AI
        </Link>

        {/* FOOTNOTE */}

        <p
          style={{
            marginTop: 20,
            fontSize: 14,
            color: "#64748B",
          }}
        >
          Learn, experiment, and create step-by-step.
        </p>
      </motion.div>

      {/* BUTTON STYLE */}

      <style>{`
        .start-btn {
          display: inline-block;
          background: #0C233F;
          color: white;
          padding: 14px 28px;
          border-radius: 18px;
          text-decoration: none;
          font-weight: 600;
          font-size: 17px;
          transition: all 0.25s ease;
          box-shadow: 0 6px 20px rgba(12,35,63,0.15);
        }

        .start-btn:hover {
          transform: translateY(-2px);
          background: #09192C;
          box-shadow: 0 10px 24px rgba(12,35,63,0.2);
        }
      `}</style>
    </main>
  );
}

/* ---------- STYLES ---------- */

const card = {
  background: "rgba(255,255,255,0.82)",
  backdropFilter: "blur(14px)",
  borderRadius: 26,
  border: "1px solid #E2E8F0",
  boxShadow:
    "0 10px 40px rgba(99,102,241,0.08)",
};

const capabilityWrapper = {
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  gap: 10,
  flexWrap: "nowrap",
  overflowX: "auto",
  marginBottom: 24,
  paddingBottom: 6,
};

const capabilityTab = {
  padding: "10px 16px",
  borderRadius: 999,
  background: "rgba(255,255,255,0.65)",
  border: "1px solid #E2E8F0",
  fontSize: 14,
  fontWeight: 600,
  color: "#475569",
  transition: "all 0.25s ease",
  backdropFilter: "blur(10px)",
  whiteSpace: "nowrap",
  flexShrink: 0,
  cursor: "pointer",
};

const activeCapabilityTab = {
  background: "#EEF2FF",
  color: "#4338CA",
  border: "1px solid #C7D2FE",
  boxShadow:
    "0 4px 14px rgba(99,102,241,0.14)",
};
